/**
 * OM_Simple — the chart overlay for the CLN colour-flip rule.
 *
 * A sibling of Om, NOT a replacement. Om is a 4H swing rule (AV_Band regime flip
 * + a Daily CLN gate + an MM trailing stop + a 30-bar time stop) and its measured
 * record — +0.92%/trade, PF 1.38, 35/48 symbols — belongs to THAT rule. None of
 * it transfers here and none of it is quoted here.
 *
 * THE RULE lives in `omSimpleEngine.ts`, a byte-identical twin of the backend
 * copy (see `scripts/check-engine-parity.mjs`). This file only supplies the CLN
 * bars and turns steps into chart furniture, so a marker on the chart and a row
 * in the SignalLog can never come from different logic.
 *
 * WHAT THIS FILE DELIBERATELY DOES NOT DO
 *   • It does not pin the length the way Om does. The premise of the strategy is
 *     "the candle changed colour", so the length must be one the user can line up
 *     with what Chart Settings draws — `settings.omSimple.clnLength`, default 50.
 *     When the two differ the overlay says so rather than drawing markers the
 *     colours do not justify.
 *   • It does not compute exits. There are none: a BUY is closed by the next
 *     SHORT. The owner places stop and target by hand after visual confirmation.
 *
 * REPAINT. Bars come from `paneData`, which is CLOSED bars only, and `linreg` at
 * bar i reads only bars <= i — so a marked bar's colour can never move. The chart
 * paints the FORMING bar's CLN colour live (TradingChart folds in
 * `rawLatestCandle`), and that colour can flip and settle elsewhere at close, so
 * no marker is ever drawn on it. A signal appears when the bar closes, not while
 * it forms.
 */
import { CandleRow } from '../../components/charts/TradingChart';
import {
  ChartStrategy, ChartStrategyOptions, StrategyOverlayResult, CandleColor, ChartMarker, StrategyNote,
} from './types';
import { computeClnBars } from '../clnCandles';
import { dropFormingHtfBars } from '../candleTransforms';
import { computeOmSimpleSteps, OM_SIMPLE_DEFAULT_CLN_LENGTH } from './omSimpleEngine';
import {
  computeOmSimplePressure, computeOmSimpleMarks,
  OM_SIMPLE_WATCH_THRESHOLD, OM_SIMPLE_CONTINUATION_THRESHOLD,
} from './omSimplePressure';

const GREEN = '#26a69a';
const RED = '#ef5350';
const NEUTRAL = '#787b86';
const MARK_BUY = '#00C853';
const MARK_SHORT = '#D50000';
// W and C use the SAME two hues as the arrows, because the colour means the
// same thing on every mark this strategy draws: the direction it points at. A W
// while long is red because the flip it warns about would be a short.
const MARK_WATCH_LONG = MARK_BUY;     // watching for an upward flip (held short)
const MARK_WATCH_SHORT = MARK_SHORT;  // watching for a downward flip (held long)

export function computeOmSimpleOverlay(
  candles: CandleRow[],
  opts?: ChartStrategyOptions,
): StrategyOverlayResult {
  if (!candles || candles.length < 2) return { candleColors: [], markers: [] };

  const raw = Number(opts?.clnLength);
  const clnLength = Number.isFinite(raw) && raw >= 2 ? Math.round(raw) : OM_SIMPLE_DEFAULT_CLN_LENGTH;

  if (candles.length <= clnLength) {
    return {
      candleColors: [], markers: [],
      notice: `OM_Simple needs more than ${clnLength} closed bars before a ${clnLength}-bar regression `
        + `means anything — this chart has ${candles.length}. Load more history or lower the CLN length.`,
    };
  }

  const cln = computeClnBars(candles, clnLength);
  const steps = computeOmSimpleSteps(cln, {
    clnLength,
    suppressVolContradiction: opts?.omSimpleSuppressThinVolume === true,
  });

  const candleColors: CandleColor[] = [];
  const markers: ChartMarker[] = [];
  const signalsByTime: NonNullable<StrategyOverlayResult['signalsByTime']> = new Map();
  const positionByTime: NonNullable<StrategyOverlayResult['positionByTime']> = new Map();
  const notesByTime: NonNullable<StrategyOverlayResult['notesByTime']> = new Map();

  // ── W / C marks ─────────────────────────────────────────────────────────
  //
  // Chart annotations, nothing more: they never touch `signalsByTime`, so the
  // trade set this strategy reports is byte-for-byte what it was before they
  // existed. See omSimplePressure.ts for what they mean and how the thresholds
  // were calibrated.
  const showWatch = opts?.omSimpleShowWatch !== false;
  const showCont = opts?.omSimpleShowContinuation !== false;

  // The higher-timeframe regime, from the CLOSED bars of the mapped HTF. `up`
  // is looked up per LTF bar by walking forward through the HTF series, which
  // is O(n) and, more importantly, causal: a bar only ever sees an HTF bar that
  // had already closed when it printed.
  let htfUp: Array<boolean | null> | null = null;
  let htfMissing = false;
  if (showCont) {
    const htfRaw = opts?.mappedHtfCandles || [];
    const htfMin = Number(opts?.mappedHtfMinutes) || 0;
    const htfClosed = htfMin > 0 ? dropFormingHtfBars(htfRaw, htfMin) : [];
    if (htfClosed.length >= clnLength + 5) {
      const htfCln = computeClnBars(htfClosed, clnLength);
      const HTF_SEC = htfMin * 60;
      htfUp = [];
      let j = -1;
      for (const st of steps) {
        while (j + 1 < htfCln.length && htfClosed[j + 1].time + HTF_SEC <= st.time) j++;
        htfUp.push(j >= clnLength ? htfCln[j].up : null);
      }
    } else {
      htfMissing = true;
    }
  }

  const wThr = Number.isFinite(Number(opts?.omSimpleWatchThreshold))
    ? Number(opts?.omSimpleWatchThreshold) : OM_SIMPLE_WATCH_THRESHOLD;
  const cThr = Number.isFinite(Number(opts?.omSimpleContinuationThreshold))
    ? Number(opts?.omSimpleContinuationThreshold) : OM_SIMPLE_CONTINUATION_THRESHOLD;

  const marks = (showWatch || showCont)
    ? computeOmSimpleMarks(
      computeOmSimplePressure(candles, cln.map(c => c.lrClose)),
      steps.map(st => st.pos),
      steps.map(st => st.signal !== null),
      htfUp,
      { watchThreshold: wThr, continuationThreshold: cThr, warmFrom: clnLength },
    )
    : [];

  const addNote = (time: number, n: StrategyNote) => {
    const cur = notesByTime.get(time);
    if (cur) cur.push(n); else notesByTime.set(time, [n]);
  };

  for (const st of steps) {
    if (st.signal === 'BUY') {
      markers.push({ time: st.time, position: 'belowBar', text: 'B', color: MARK_BUY, shape: 'arrowUp', size: 2 });
      signalsByTime.set(st.time, { signal: 'BUY', strength: null, pos: 'LONG', reason: st.reason });
    } else if (st.signal === 'SHORT') {
      markers.push({ time: st.time, position: 'aboveBar', text: 'S', color: MARK_SHORT, shape: 'arrowDown', size: 2 });
      signalsByTime.set(st.time, { signal: 'SHORT', strength: null, pos: 'SHORT', reason: st.reason });
    }
    // On a CLN chart these are suppressed by TradingChart (the regression colour
    // is the whole point of that candle type and must stay the dominant read);
    // on any other candle type they show the running position instead.
    const col = st.pos === 'LONG' ? GREEN : st.pos === 'SHORT' ? RED : NEUTRAL;
    candleColors.push({ time: st.time, upColor: col, downColor: col });
    positionByTime.set(st.time, st.pos);
  }

  // AFTER the B/S loop on purpose: markers on one bar stack in emission order,
  // so the entry arrow sits nearest the candle and the informational circle outside it.
  for (let i = 0; i < steps.length; i++) {
    const st = steps[i];
    const mk = marks[i];
    if (mk && showWatch && mk.watch) {
      // The flip it warns about would go THIS way, so that is the colour and
      // the side. Held long -> a red W above the bar.
      const long = mk.watch === 'LONG';
      markers.push({
        time: st.time, position: long ? 'belowBar' : 'aboveBar', text: 'W',
        color: long ? MARK_WATCH_LONG : MARK_WATCH_SHORT, shape: 'circle',
      });
      addNote(st.time, {
        code: 'W', label: `WATCH — the ${long ? 'short' : 'long'} leg is weakening`,
        tip: `Pressure for a ${long ? 'BUY' : 'SHORT'} flip has built to ${mk.watchScore} of 100 while the `
          + `position is still ${long ? 'SHORT' : 'LONG'}. Not a trade and not an exit — a heads-up that this leg may be ending. `
          + 'Measured at these settings, a flip follows within 10 bars 70% of the time after a W, against a 45% base rate.',
        color: long ? MARK_WATCH_LONG : MARK_WATCH_SHORT, score: mk.watchScore ?? undefined,
      });
    }
    if (mk && showCont && mk.continuation) {
      const long = mk.continuation === 'LONG';
      markers.push({
        time: st.time, position: long ? 'belowBar' : 'aboveBar', text: 'C',
        color: long ? MARK_BUY : MARK_SHORT, shape: 'circle',
      });
      addNote(st.time, {
        code: 'C', label: `CONTINUATION — the ${long ? 'long' : 'short'} leg is resuming`,
        tip: `Trend pressure for the ${long ? 'LONG' : 'SHORT'} you already hold has built to ${mk.continuationScore} of 100, `
          + 'and the higher timeframe agrees. This is a reason to keep holding, not a new entry — '
          + 'OM_Simple is always in the market, so a continuation can only ever point the way you are already positioned.',
        color: long ? MARK_BUY : MARK_SHORT, score: mk.continuationScore ?? undefined,
      });
    }
  }


  // The notice, assembled from whatever is actually true, so the user is never
  // looking at markers whose colour change is not on screen. Each entry is ONE
  // short line: the badge that renders these draws every line unwrapped, so a
  // paragraph would run off the pane.
  const notes: string[] = [];
  if (opts?.candleType && opts.candleType !== 'CLN') {
    notes.push(`Chart is drawing ${opts.candleType} candles — signals are still correct,`);
    notes.push('but switch the candle type to CLN to SEE the colour changes.');
  }
  const drawn = Number(opts?.chartClnLength);
  if (Number.isFinite(drawn) && Math.round(drawn) !== clnLength) {
    notes.push(`Signals use CLN length ${clnLength}; this chart draws ${Math.round(drawn)}.`);
    notes.push('Markers will not line up with the colours until these match.');
  }

  // A C without its higher timeframe is a different claim from a confirmed one,
  // so it is suppressed rather than downgraded — and the chart says why instead
  // of the marks simply being absent.
  if (htfMissing) {
    notes.push('No higher-timeframe history here — C (continuation) marks are hidden.');
  }

  return {
    candleColors,
    markers,
    signalsByTime,
    positionByTime,
    notesByTime: notesByTime.size ? notesByTime : undefined,
    notice: notes.length ? notes.join('\n') : undefined,
  };
}

export const omSimpleStrategy: ChartStrategy = {
  id: 'om_simple',
  name: 'OM_Simple',
  computeOverlays(candles: CandleRow[], opts?: ChartStrategyOptions): StrategyOverlayResult {
    return computeOmSimpleOverlay(candles, opts);
  },
};
